import { skipToken } from '@reduxjs/toolkit/query/react';

import {
  useGetGscConnectionStatusQuery,
  useGetGscSourceDetailQuery,
  type GscSourceStatus,
} from '../api/gscConnectionApi';
import { readGscConnectionState } from '../GscConnectionState';

// Fast enough that a reconnect (stamped within seconds) flips to the dashboard
// promptly, cheap enough to leave running while a first sync takes hours.
const FIRST_SYNC_POLL_INTERVAL_MS = 15_000;

export type GscSyncState = {
  isStatusLoading: boolean;
  /**
   * True until enough is known to tell the connect flow from the dashboard.
   * Render neither view while it holds, or the connect flow flashes for
   * returning users whose sync already completed. See the computation below
   * for the "why now"; see `AGENTS.md` for the full subtlety.
   */
  isResolving: boolean;
  /** An active connection that has brought data in at least once. */
  hasSyncedConnection: boolean;
  activeSources: GscSourceStatus[];
  connectedSource: GscSourceStatus | null;
  /** `/gsc/status` aggregate. Null when older rest-api omits it. */
  connectedAccountEmail: string | null;
  /**
   * A previously-synced connection stopped working for an external reason
   * (Google revoked the token) rather than a deliberate customer disconnect.
   * Implies there is prior data worth keeping the dashboard visible for — see
   * `readGscConnectionState`.
   */
  isBroken: boolean;
  /**
   * The last date a revoked domain actually brought data in — what the
   * reconnect alert dates its "stopped updating" wording from. Live siblings
   * do not move this stamp.
   */
  brokenSinceAt: string | null;
  lostUrls: string[];
  lostSinceAt: string | null;
  isLostOnly: boolean;
  refetchStatus: ReturnType<typeof useGetGscConnectionStatusQuery>['refetch'];
};

/**
 * The tenant's GSC sync state, shared by the two routes that branch on it: the
 * dashboard (which redirects when nothing has synced) and the connect flow
 * (which leaves for the dashboard once a sync lands).
 */
function useGscSyncState(): GscSyncState {
  const { data: status, isLoading: isStatusLoading, refetch: refetchStatus } = useGetGscConnectionStatusQuery(
    undefined,
    { refetchOnMountOrArgChange: true }
  );
  const connectionState = readGscConnectionState(status?.sources ?? [], status?.connectedAccountEmail);
  // The status list also carries companion PRERENDER sources; the connection
  // flow is about the GSC half only.
  const activeSources =
    status?.sources.filter((s) => s.type === 'GSC' && s.status === 'ACTIVE' && s.propertyUrl !== null) ?? [];
  const connectedSource = activeSources[0] ?? null;
  // `currentData`, not `data`: RTK Query's `data` is the last result for *any*
  // arg, so while the id switches it can still be the previous source's — read
  // as a `lastSyncAt` that belongs to a sync this source never ran.
  const { currentData: sourceDetail, isError: isSourceDetailError } = useGetGscSourceDetailQuery(
    connectedSource?.id ?? skipToken,
    { refetchOnMountOrArgChange: true }
  );
  // Second subscription to the same cache entry, polling only until the first
  // sync stamp appears. The detail is otherwise fetched once, right after
  // activation — which races visibility stamping lastSyncAt (seconds later on
  // a reconnect, where data just resumes) — and nothing else refetches it
  // in-session, so the flow sat on the sync step until a remount.
  const isAwaitingFirstSync =
    connectedSource !== null && sourceDetail !== undefined && sourceDetail.lastSyncAt === null;
  useGetGscSourceDetailQuery(isAwaitingFirstSync ? connectedSource.id : skipToken, {
    pollingInterval: FIRST_SYNC_POLL_INTERVAL_MS,
  });

  return {
    isStatusLoading,
    // Keyed off the absence of the detail rather than its `isLoading`: the
    // query is dispatched from an effect, so on the render where
    // `connectedSource` first appears the detail is merely uninitialised —
    // `isLoading` is still false there, which would let a view commit for one
    // frame. `!isSourceDetailError` resolves a failed request instead of
    // spinning forever.
    isResolving:
      isStatusLoading || (connectedSource !== null && sourceDetail === undefined && !isSourceDetailError),
    hasSyncedConnection: connectedSource !== null && sourceDetail?.lastSyncAt != null,
    activeSources,
    connectedSource,
    connectedAccountEmail: status?.connectedAccountEmail ?? null,
    isBroken: connectionState.isBroken,
    brokenSinceAt: connectionState.brokenSinceAt,
    lostUrls: connectionState.lostUrls,
    lostSinceAt: connectionState.lostSinceAt,
    isLostOnly: connectionState.lostUrls.length > 0 && connectionState.liveDomains.length === 0,
    refetchStatus,
  };
}

export default useGscSyncState;
